// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { create as createCML, getInnerText as getCMLInnerText, getNodeInnerText } from 'bundles/cml';
import { typeNames } from 'bundles/compound-assessments/constants';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import {
  GradeFeedbackStrings,
  getTranslation,
} from 'bundles/compound-assessments/lib/utils/getQuestionGradeFeedbackInfo';
import type {
  CMLOrHTMLContent,
  ExplainedFillableBlankFeedbackResponse,
  FeedbackDetailLevel,
  FillableBlankResponse,
  GradedFillableBlankFeedbackResponse,
  MultipleFillableBlankFeedbackResponse,
  MultipleFillableBlanksPrompt,
  MultipleFillableBlanksResponse,
} from 'bundles/compound-assessments/types/FormParts';

import _t from 'i18n!nls/compound-assessments';

export const getFillableBlankAriaText = (
  responseIndex: string,
  isReadOnly: boolean,
  showFeedback: boolean,
  selected?: string,
  isCorrect?: boolean,
  feedbackLevel?: FeedbackDetailLevel
) => {
  if (!isReadOnly && !showFeedback) {
    return selected
      ? _t('Response area number #{responseIndex}.', { responseIndex })
      : _t('Response area number #{responseIndex}. Select an answer.', { responseIndex });
  } else if (isReadOnly && !showFeedback) {
    return selected
      ? _t('Response number #{responseIndex} selected.', { responseIndex })
      : _t('Response number #{responseIndex} not selected.');
  } else if (showFeedback && feedbackLevel === 'Full') {
    if (selected) {
      return isCorrect
        ? _t('Response number #{responseIndex} selected. Correct', { responseIndex })
        : _t('Response number #{responseIndex} selected. Incorrect', { responseIndex });
    } else {
      return _t('Response number #{responseIndex} not selected. Incorrect.', { responseIndex });
    }
  } else if (showFeedback) {
    if (selected) {
      return _t('Response number #{responseIndex} selected.', { responseIndex });
    } else {
      return _t('Response number #{responseIndex} not selected.', { responseIndex });
    }
  } else {
    return _t('Response area number #{responseIndex}.', { responseIndex });
  }
};

export const getFillableBlankCount = (prompt?: MultipleFillableBlanksPrompt) => {
  return prompt?.variant?.definition?.fillableBlanks?.length || 0;
};

export const isResponseInvalid = (response?: MultipleFillableBlanksResponse, prompt?: MultipleFillableBlanksPrompt) => {
  const fillableBlankIds = prompt?.variant?.definition?.fillableBlanks?.map(
    (fillableBlank) => fillableBlank.definition.id
  );

  const responses = response?.definition?.value?.fillableBlankResponses;

  if (!fillableBlankIds || !responses) {
    return true;
  }

  return fillableBlankIds.some((id) => responses[id] === undefined);
};

export const cleanResponse = (
  response?: MultipleFillableBlanksResponse
): MultipleFillableBlanksResponse | undefined => {
  const fillableBlankResponses = response?.definition?.value?.fillableBlankResponses;

  if (!fillableBlankResponses) {
    return undefined;
  }

  const newFillableBlankResponses = { ...fillableBlankResponses };
  delete newFillableBlankResponses.__typename; // this field comes back from Apollo and messes with the ID -> response map

  return {
    typeName: typeNames.AUTO_GRADABLE_RESPONSE,
    definition: {
      value: {
        fillableBlankResponses: newFillableBlankResponses,
      },
    },
  };
};

export const getHandleChange =
  (
    uuid: string,
    getResponse: () => MultipleFillableBlanksResponse | undefined,
    onChange?: (newResponse: MultipleFillableBlanksResponse) => void
  ) =>
  (selectedAnswerOptionId: string) => {
    const response = cleanResponse(getResponse());
    const fillableBlankResponses = response?.definition?.value?.fillableBlankResponses;

    if (!uuid || !fillableBlankResponses) {
      return;
    }

    const newFillableBlankResponses = { ...fillableBlankResponses };
    const newFillableBlankResponse: FillableBlankResponse = {
      typeName: 'multipleChoiceFillableBlankResponse',
      definition: {
        response: selectedAnswerOptionId,
      },
    };

    newFillableBlankResponses[uuid] = newFillableBlankResponse;

    const updatedResponse = {
      typeName: typeNames.AUTO_GRADABLE_RESPONSE,
      definition: {
        value: {
          fillableBlankResponses: newFillableBlankResponses,
        },
      },
    };

    onChange?.(updatedResponse);
  };

export const richTextToString = (content: CMLOrHTMLContent) => {
  if (content.typeName === 'htmlText') {
    const template = document.createElement('template');
    const html = content.definition.content.trim(); // Never return a text node of whitespace as the result
    template.innerHTML = html;
    const element = template.content.firstChild;
    return element ? getNodeInnerText(element) : '';
  } else {
    return getCMLInnerText(createCML(content.definition.value));
  }
};

export const getFillableBlankFeedbackLabel = (
  feedback?: ExplainedFillableBlankFeedbackResponse | GradedFillableBlankFeedbackResponse | null,
  showNotAnsweredLabel?: boolean
) => {
  if (showNotAnsweredLabel) {
    return getTranslation(GradeFeedbackStrings.NOT_ANSWERED);
  }

  return feedback?.isCorrect
    ? getTranslation(GradeFeedbackStrings.CORRECT)
    : getTranslation(GradeFeedbackStrings.INCORRECT);
};

type GetFillableBlankFeedbackProps = {
  prompt?: MultipleFillableBlanksPrompt;
  fillableBlankId?: string;
};

export const getFillableBlankFeedback = ({ prompt, fillableBlankId }: GetFillableBlankFeedbackProps) => {
  const feedbackDetailLevel = prompt?.variant?.detailLevel;
  const promptFeedbackDefinition = prompt?.feedback?.definition as MultipleFillableBlankFeedbackResponse;

  if (feedbackDetailLevel === 'Limited') {
    return null;
  }

  const fillableBlankFeedbacks = (
    feedbackDetailLevel === 'Full'
      ? promptFeedbackDefinition?.explainedBlankFeedbacks
      : promptFeedbackDefinition?.gradedBlankFeedbacks
  ) as ExplainedFillableBlankFeedbackResponse[];
  const fillableBlankFeedback = fillableBlankFeedbacks?.find(
    (feedbackItem) => feedbackItem.blankId === fillableBlankId
  );

  return fillableBlankFeedback;
};
